import React, { useState, useEffect } from 'react';
import { GitCommit, GitBranch, FolderTree, AlertCircle } from 'lucide-react';

export default function RepoPanel() {
  const [commits, setCommits] = useState([]);
  const [branches, setBranches] = useState([]);
  const [structure, setStructure] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const bu = process.env.REACT_APP_BACKEND_URL || '';
    
    Promise.all([
      fetch(`${bu}/api/github/commits?count=6`).then(r => r.json()).catch(() => ({})),
      fetch(`${bu}/api/github/branches`).then(r => r.json()).catch(() => ({})),
      fetch(`${bu}/api/github/structure`).then(r => r.json()).catch(() => ({})),
    ]).then(([c, b, s]) => {
      setCommits(c.commits || []);
      setBranches(b.branches || []);
      setStructure((s.structure || []).filter(i => i.type === 'dir').slice(0, 8));
      setLoading(false);
    });

    // Refresh every 60 seconds
    const interval = setInterval(() => {
      fetch(`${bu}/api/github/commits?count=6`).then(r => r.json()).then(c => setCommits(c.commits || [])).catch(() => {});
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const panelStyle = {
    position: 'fixed', top: 56, right: 16, width: 280, zIndex: 30,
    background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(0,215,255,0.1)', borderRadius: 14,
    padding: '14px 16px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
    maxHeight: 'calc(100vh - 130px)', overflowY: 'auto',
  };

  const sectionTitle = { color: '#00D7FF', fontWeight: 700, letterSpacing: '0.2em', marginBottom: 8, fontSize: 9 };
  const divider = { borderTop: '1px solid rgba(0,215,255,0.08)', paddingTop: 10, marginTop: 8 };

  if (loading) {
    return (
      <div style={panelStyle}>
        <div style={sectionTitle}>◈ REPOSITORY</div>
        <div style={{ color: '#7C8AA6', textAlign: 'center', padding: '20px 0' }}>SCANNING REPO...</div>
      </div>
    );
  }

  return (
    <div style={panelStyle}>
      {/* Repo header */}
      <div style={sectionTitle}>◈ NEXIS365-V2</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
        <span style={{ background: 'rgba(25,242,214,0.12)', color: '#19F2D6', padding: '1px 6px', borderRadius: 3, fontSize: 9 }}>TypeScript</span>
        <span style={{ background: 'rgba(57,255,182,0.12)', color: '#39FFB6', padding: '1px 6px', borderRadius: 3, fontSize: 9 }}>main</span>
        <span style={{ background: 'rgba(0,215,255,0.12)', color: '#00D7FF', padding: '1px 6px', borderRadius: 3, fontSize: 9 }}>Private</span>
      </div>

      {/* Recent Commits */}
      <div style={divider}>
        <div style={{ ...sectionTitle, display: 'flex', alignItems: 'center', gap: 6 }}>
          <GitCommit size={10} /> RECENT COMMITS
        </div>
        {commits.slice(0, 5).map((c, i) => (
          <div key={i} style={{ marginBottom: 6, paddingLeft: 4 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
              <span style={{ color: '#FFB020', fontSize: 9, flexShrink: 0, marginTop: 1 }}>{c.sha}</span>
              <span style={{ color: '#B9F3FF', fontSize: 9, lineHeight: 1.3 }}>{c.message?.substring(0, 45)}{c.message?.length > 45 ? '...' : ''}</span>
            </div>
            <div style={{ color: '#555', fontSize: 8, marginTop: 1, paddingLeft: 0 }}>
              {c.author} · {c.date ? new Date(c.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : ''}
              {c.additions > 0 && <span style={{ color: '#39FFB6' }}> +{c.additions}</span>}
              {c.deletions > 0 && <span style={{ color: '#FF3B3B' }}> -{c.deletions}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Branches */}
      <div style={divider}>
        <div style={{ ...sectionTitle, display: 'flex', alignItems: 'center', gap: 6 }}>
          <GitBranch size={10} /> BRANCHES
        </div>
        {branches.slice(0, 5).map((b, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ color: '#B9F3FF' }}>{b.name}</span>
            {b.protected && <span style={{ color: '#FFB020', fontSize: 8 }}>PROTECTED</span>}
          </div>
        ))}
      </div>

      {/* Structure */}
      <div style={divider}>
        <div style={{ ...sectionTitle, display: 'flex', alignItems: 'center', gap: 6 }}>
          <FolderTree size={10} /> STRUCTURE
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {structure.map((d, i) => (
            <span key={i} style={{
              background: 'rgba(0,215,255,0.06)', border: '1px solid rgba(0,215,255,0.12)',
              color: '#7C8AA6', padding: '2px 6px', borderRadius: 4, fontSize: 9,
            }}>
              {d.name}/
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
